// src/pages/TwoPieceStyleDetail.jsx
import React, { useState, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { TWO_PIECE_STYLES } from "../data/twoPieceStyles";
import MediaLightbox from "../components/MediaLightbox";
import Seo from "../components/Seo";

/* ---------- Per-wheel CTA copy (edit anytime) ---------- */
const CTA_BY_SLUG = {
  t1: "T1 — Classic flat-face 5-spoke with lip depth options. 2-piece lets you dial stance, lip, and hardware.",
  t2: "T2 — Modern, sharp split 5-spoke in a 2-piece format for deeper lips and tailored concavity.",
  t3: "T3 — Lightweight 10-spoke motorsport aesthetic with spun barrel for aggressive profiles.",
  t4: "T4 — Clean face with floating-hardware look; step or reverse lip available.",
  t5: "T5 — Deep lip with exposed hardware for an aggressive, show-ready stance.",
  t6: "T6 — Y-split silhouette paired with a step-lip profile for depth and presence.",
};
const ctaFor = (slug, name) =>
  CTA_BY_SLUG[slug] ||
  `${name} — Custom-forged 2-piece (forged center + spun barrel) built to your specs. See our samples below.`;

/* ---------- CRA/Webpack gallery loader (per slug, tolerant of missing folders) ---------- */
function importAll(ctx) {
  return ctx
    .keys()
    .sort() // 01, 02, 03 ...
    .map((key) => {
      const file = key.replace("./", "");
      const isVideo = /\.(mp4|mov|webm)$/i.test(file);
      return { type: isVideo ? "video" : "image", src: ctx(key) };
    });
}

// Sample folders: src/assets/samples/t1, t2, ... t6
const SAMPLE_CONTEXTS = {};
try { SAMPLE_CONTEXTS.t1 = require.context("../assets/samples/t1", false, /\.(png|jpe?g|webp|mp4|mov|webm)$/i); } catch {}
try { SAMPLE_CONTEXTS.t2 = require.context("../assets/samples/t2", false, /\.(png|jpe?g|webp|mp4|mov|webm)$/i); } catch {}
try { SAMPLE_CONTEXTS.t3 = require.context("../assets/samples/t3", false, /\.(png|jpe?g|webp|mp4|mov|webm)$/i); } catch {}
try { SAMPLE_CONTEXTS.t4 = require.context("../assets/samples/t4", false, /\.(png|jpe?g|webp|mp4|mov|webm)$/i); } catch {}
try { SAMPLE_CONTEXTS.t5 = require.context("../assets/samples/t5", false, /\.(png|jpe?g|webp|mp4|mov|webm)$/i); } catch {}
try { SAMPLE_CONTEXTS.t6 = require.context("../assets/samples/t6", false, /\.(png|jpe?g|webp|mp4|mov|webm)$/i); } catch {}

export default function TwoPieceStyleDetail() {
  // Accept several param names so we don't break if the route uses :slug or :id
  const params = useParams();
  const styleSlug = params.styleSlug || params.slug || params.id;
  const style = TWO_PIECE_STYLES.find((s) => s.slug === styleSlug);

  const samples = useMemo(() => {
    const ctx = SAMPLE_CONTEXTS[styleSlug];
    return ctx ? importAll(ctx) : [];
  }, [styleSlug]);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const openAt = useCallback((i) => { setActiveIndex(i); setLightboxOpen(true); }, []);
  const close  = useCallback(() => setLightboxOpen(false), []);
  const prev   = useCallback(() => setActiveIndex((i) => (i - 1 + samples.length) % samples.length), [samples.length]);
  const next   = useCallback(() => setActiveIndex((i) => (i + 1) % samples.length), [samples.length]);

  if (!style) {
    return (
      <div className="max-w-5xl mx-auto p-6 text-neutral-900">
        <p className="opacity-80">Style not found.</p>
        <Link to="/wheels/forged-2-piece" className="underline">Back to 2-Piece</Link>
      </div>
    );
  }

  const CTA_TEXT = ctaFor(styleSlug, style.name);

  // ---- SEO ----
  const pageTitle = `Unlimited Levels ${style?.name || styleSlug.toUpperCase()} | Forged 2-Piece Wheel`;
  const pageDesc =
    (style?.seoDescription || style?.blurb || CTA_TEXT || "Unlimited Levels forged 2-piece wheels built without limits.")
      .replace(/\s+/g, " ")
      .trim();
  const pageUrl = `https://unlimitedlevels.co/wheels/forged-2-piece/${styleSlug}`;
  const ogImage = "https://unlimitedlevels.co/assets/og/unlimited-levels-hero-1200x630.jpg";
  const productLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: `Unlimited Levels ${style?.name || styleSlug.toUpperCase()} Forged 2-Piece Wheel`,
    brand: { "@type": "Brand", name: "Unlimited Levels" },
    image: [ogImage],
    description: pageDesc,
    category: "AutomotivePart",
    url: pageUrl,
    offers: { "@type": "Offer", price: "0.00", priceCurrency: "USD", availability: "https://schema.org/InStock" }
  };

  return (
    <>
      <Seo title={pageTitle} description={pageDesc} canonical={pageUrl} ogImage={ogImage} ogUrl={pageUrl} jsonLd={productLd} />

      <div className="max-w-6xl mx-auto p-6 text-neutral-900">
        <Link to="/wheels/forged-2-piece" className="text-sm underline">← Back to 2-Piece</Link>

        <div className="grid md:grid-cols-2 gap-8 mt-6">
          {/* Left: big product image */}
          <div className="bg-neutral-100 rounded-2xl overflow-hidden">
            <img src={style.img} alt={style.name} className="w-full h-auto" />
          </div>

          {/* Right: title, blurb, CTA text + Request button */}
          <div>
            <h1 className="text-3xl font-bold">{style.name}</h1>
            <p className="mt-2 text-neutral-700">{style.blurb}</p>

            <ul className="mt-6 space-y-2 text-sm text-neutral-700">
              <li>• Forged 6061-T6 aluminum center + spun-forged barrel</li>
              <li>• 18&quot; – 30&quot; diameters, custom widths and offsets</li>
              <li>• Step or reverse lip; exposed or hidden hardware</li>
              <li>• Brake-clearance engineered per vehicle</li>
              <li>• Finishes: Painted, Brushed, Polished, Tinted Clear, Raw + Clear</li>
              <li>• Lifetime Structural Warranty*</li>
              <li>• Average production time: 4–6 weeks</li>
            </ul>

            <p className="mt-6 text-base md:text-lg text-neutral-900">{CTA_TEXT}</p>

            <Link
              to="/quote"
              className="inline-flex items-center justify-center mt-4 px-6 py-3 rounded-xl bg-black text-white font-semibold shadow-lg hover:bg-neutral-800 active:translate-y-px transition"
            >
              Request a Quote
            </Link>
          </div>
        </div>

        {/* Samples / Lookbook */}
        {samples.length > 0 ? (
          <>
            <h3 className="mt-12 mb-3 text-xl font-semibold tracking-tight">Lookbook — real builds & samples</h3>
            <p className="mb-6 text-sm text-neutral-600">
              Click any image or video to view full screen. Use ◀ ▶ keys, or Esc to close.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {samples.map((m, i) => (
                <button
                  key={m.src}
                  onClick={() => openAt(i)}
                  className="group relative overflow-hidden rounded-xl bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-neutral-900"
                  aria-label="Open sample"
                >
                  {m.type === "image" ? (
                    <img
                      src={m.src}
                      alt=""
                      className="h-36 w-full object-cover group-hover:scale-105 transition-transform"
                      loading="lazy"
                    />
                  ) : (
                    <div className="h-36 w-full overflow-hidden">
                      <video
                        src={m.src}
                        className="h-full w-full object-cover"
                        muted
                        loop
                        playsInline
                        autoPlay
                      />
                      <span className="absolute bottom-2 right-2 text-[11px] bg-black/60 text-white px-2 py-0.5 rounded">
                        video
                      </span>
                    </div>
                  )}
                </button>
              ))}
            </div>

            {lightboxOpen && (
              <MediaLightbox
                items={samples}
                index={activeIndex}
                onClose={close}
                onPrev={prev}
                onNext={next}
              />
            )}
          </>
        ) : (
          <p className="mt-12 text-sm text-neutral-500">
            No samples for <span className="font-semibold">{styleSlug}</span> yet.
            Add files to <code className="mx-1">src/assets/samples/{styleSlug}/</code> and restart the dev server.
          </p>
        )}
      </div>
    </>
  );
}
